/**
 * Copyright 2025 SweetCode. All rights reserved.
 */

(function (pmw, undefined) {

	/**
	 * SSP (Server Side Proxy) state for the current session.
	 *
	 * - null: not yet tested
	 * - true: SSP verified and working
	 * - false: SSP failed, using fallback
	 *
	 * Persisted via pmw.storeData/retrieveData (sessionStorage) so that
	 * once SSP is verified on one page load, subsequent navigations in
	 * the same tab skip re-verification.
	 *
	 * Initialized in _initSspState() after pmw.storeData is available.
	 *
	 * @type {boolean|null}
	 */
	let _sspVerified = null

	/**
	 * Whether we are currently in the process of verifying SSP.
	 * Prevents duplicate verification requests.
	 *
	 * @type {boolean}
	 */
	let _sspVerifying = false

	/**
	 * Queue of payloads waiting for SSP verification to complete.
	 *
	 * @type {Array}
	 */
	let _sspPendingQueue = []

	/**
	 * Read SSP verification state from session storage.
	 * Must be called after pmw.retrieveData is available (i.e. not at module init).
	 *
	 * Also checks for quota exhaustion from both the server-rendered
	 * data layer and any previously stored session flag.
	 */
	const _initSspState = () => {
		if (_sspVerified !== null) return // already set (either from storage or from a verify call)

		// Check server-rendered quota flag (set during PHP sync)
		if (pmwDataLayer?.general?.ssp?.quota_exceeded) {
			pmw.console.log("SSP quota exceeded (data layer flag)")
			_sspVerified = false
			return
		}

		// Check session-level quota flag (set when a 403 was received)
		if (pmw.retrieveData("ssp_quota_exceeded") === true) {
			pmw.console.log("SSP quota exceeded (session flag)")
			_sspVerified = false
			return
		}

		const stored = pmw.retrieveData("ssp_verified")
		if (stored === true) _sspVerified = true
		else if (stored === false) _sspVerified = false
	}

	const isSspEnabled = () => {
		return !!pmwDataLayer?.general?.ssp?.active && !!pmwDataLayer?.general?.ssp?.events_url
	}

	const isSspFallbackEnabled = () => {
		return pmwDataLayer?.general?.ssp?.fallback_to_wc !== false
	}

	/**
	 * Known proxy / translation service hostname suffixes whose requests the
	 * SSP will always reject because their Origin header won't match the
	 * configured shop domain.  Silently dropping them client-side avoids
	 * wasted network round-trips and noisy Event Viewer entries.
	 *
	 * Real mis-configurations (e.g. .co vs .com) are intentionally NOT
	 * blocked here so they still surface in the SSP Event Viewer as
	 * actionable diagnostics.
	 */
	const _sspBlockedOriginSuffixes = [
		".translate.goog",
		".webcache.googleusercontent.com",
	]

	/**
	 * Returns true when the current hostname ends with a known proxy
	 * suffix that the SSP would reject.
	 *
	 * @return {boolean}
	 */
	const isSspOriginBlocked = () => {
		const host = window.location.hostname.toLowerCase()
		return _sspBlockedOriginSuffixes.some(suffix => host.endsWith(suffix))
	}

	/**
	 * Build SSP request headers including security tokens.
	 *
	 * @return {object} Headers object for fetch()
	 */
	const getSspHeaders = () => {
		const headers = {"Content-Type": "application/json"}

		const token = pmwDataLayer?.general?.ssp?.domain_token
		if (token) {
			headers["X-SSP-Token"] = token
		}

		const sessionId = pmwDataLayer?.general?.ssp?.session_id
		if (sessionId) {
			headers["X-SSP-Session"] = sessionId
		}

		return headers
	}

	/**
	 * Send payload to the SSP proxy.
	 *
	 * Uses fetch() with keepalive for all requests (no sendBeacon)
	 * to support custom headers for traffic verification.
	 *
	 * Detects quota exhaustion (403 + request_limit_exceeded) and
	 * stops further SSP requests for the current session.
	 *
	 * @param {object} payload
	 * @return {Promise<boolean>} True if sent successfully.
	 */
	const sendPayloadToSsp = async (payload) => {
		const sspUrl = pmwDataLayer.general.ssp.events_url

		try {
			const response = await fetch(sspUrl, {
				method     : "POST",
				credentials: "include",
				headers    : getSspHeaders(),
				body       : JSON.stringify(payload),
				keepalive  : true,
				redirect   : "error",
			})

			if (response.ok) {
				return true
			}

			// Check for quota exhaustion (403 + request_limit_exceeded)
			if (response.status === 403) {
				try {
					const body = await response.json()
					if (body?.error?.code === "request_limit_exceeded") {
						pmw.console.log("SSP quota exceeded — stopping SSP for this session")
						_sspVerified = false
						pmw.storeData("ssp_quota_exceeded", true)
					}
				} catch (_) {
					// JSON parse failed — treat as generic failure
				}
			}

			pmw.console.log(`SSP fetch: failed (${response.status})`)
			return false
		} catch (e) {
			pmw.console.error("Server-side send failed (SSP):", e)
			return false
		}
	}

	/**
	 * Verify that the SSP proxy is reachable by sending the first payload.
	 *
	 * Also detects quota exhaustion from the response.
	 *
	 * @param {object} payload
	 * @return {Promise<boolean>}
	 */
	const verifySsp = async (payload) => {
		const sspUrl = pmwDataLayer.general.ssp.events_url

		try {
			const response = await fetch(sspUrl, {
				method     : "POST",
				credentials: "include",
				headers    : getSspHeaders(),
				body       : JSON.stringify(payload),
				keepalive  : true,
				redirect   : "error",
			})

			if (response.ok) return true

			// Check for quota exhaustion (403 + request_limit_exceeded)
			if (response.status === 403) {
				try {
					const body = await response.json()
					if (body?.error?.code === "request_limit_exceeded") {
						pmw.console.log("SSP quota exceeded during verification")
						pmw.storeData("ssp_quota_exceeded", true)
					}
				} catch (_) {
					// JSON parse failed — treat as generic failure
				}
			}

			return false
		} catch (e) {
			pmw.console.error("Server-side send failed (SSP verification):", e)
			return false
		}
	}

	/**
	 * Process any payloads that were queued during SSP verification.
	 */
	const drainSspQueue = () => {
		const queue = _sspPendingQueue.splice(0)

		// Terminal catches: none of these sends is awaited, so a rejection
		// would otherwise escape as an unhandled promise rejection.
		for (const queuedPayload of queue) {
			if (_sspVerified) {
				sendPayloadToSsp(queuedPayload).catch(e => {
					pmw.console.error("Server-side send failed (SSP):", e)
				})
			} else if (isSspFallbackEnabled()) {
				sendPayloadViaWc(queuedPayload).catch(e => {
					pmw.console.error("Server-side send failed:", e)
				})
			} else {
				pmw.console.log("SSP unreachable, dropping event (fallback disabled)")
			}
		}
	}

	/**
	 * Settle a verification chain that failed outright the same way as an
	 * unreachable SSP: mark it failed for the session and drain the queue
	 * through the existing fallback path.
	 *
	 * Every step is guarded, because this runs inside the chain's terminal
	 * catch, where a throw would escape as an unhandled promise rejection.
	 *
	 * @since 1.69.3
	 */
	/**
	 * The payload a failed SSP verification carried was never accepted by the
	 * SSP, and only the payloads queued behind it are drained, so it used to be
	 * lost: usually the session's first event. Send it through the WC fallback
	 * like every later event, or drop it with the same log line when the
	 * fallback is off. Each payload is re-sent at most once.
	 *
	 * @since 1.69.3
	 */
	const resentVerificationPayloads = new WeakSet()

	const resendVerificationPayload = (verificationPayload) => {
		// A WeakSet rather than a flag on the payload: the payload is serialized
		// as is, and a marker property would travel to the server.
		if (resentVerificationPayloads.has(verificationPayload)) return
		resentVerificationPayloads.add(verificationPayload)

		if (isSspFallbackEnabled()) {
			sendPayloadViaWc(verificationPayload).catch(e => {
				pmw.console.error("Server-side send failed:", e)
			})
		} else {
			pmw.console.log("SSP unreachable, dropping event (fallback disabled)")
		}
	}

	const failSspVerification = (verificationPayload = null) => {
		_sspVerified  = false
		_sspVerifying = false

		if (verificationPayload) resendVerificationPayload(verificationPayload)

		try {
			pmw.storeData("ssp_verified", false)
		} catch (e) {
			// The in-memory state above still routes this page's events.
		}

		try {
			drainSspQueue()
		} catch (e) {
			pmw.console.error("Server-side send failed (SSP queue drain):", e)
		}
	}

	/**
	 * Send a payload to the WooCommerce S2S endpoint (REST or wp-ajax fallback).
	 *
	 * When REST is already confirmed (cached true), uses navigator.sendBeacon for
	 * small payloads (< 60 KB) or fetch with keepalive for larger ones.
	 *
	 * When REST status is unknown, uses fetch so we can inspect the response and
	 * cache the result. If that first fetch fails, falls back to AJAX immediately
	 * so the event is never lost.
	 *
	 * @param {object} payload
	 */
	const sendPayloadViaWc = async (payload) => {

		const blob    = new Blob([JSON.stringify(payload)], {type: "application/json; charset=UTF-8"})
		const restUrl = pmw.root + "pmw/v1/sse/"

		// Fast path: REST already confirmed working — fire-and-forget
		if (pmw.getRestStatus() === true) {
			if (blob.size < 60000 && typeof navigator.sendBeacon === "function") {
				const success = navigator.sendBeacon(restUrl, blob)
				success ? pmw.console.log("navigator.sendBeacon success") : pmw.console.error("navigator.sendBeacon failed")
			} else {
				fetch(restUrl, {
					method     : "POST",
					credentials: "same-origin",
					headers    : {"Content-Type": "application/json"},
					body       : JSON.stringify(payload),
					keepalive  : true,
					redirect   : "error",
				})
					.then(response => response.json())
					.then(message => {
						message.success ? pmw.console.log(message) : pmw.console.error(message)
					})
					.catch(error => {
						pmw.console.error("Server-side send failed:", error)
					})
			}
			return
		}

		// Unknown or failed REST status — use tryRestOrAjax to probe + fallback
		// Post the payload as one JSON string. jQuery's form encoding turns
		// every number into a string, and strict APIs (OpenAI, Pinterest,
		// Snapchat, Reddit) rejected those events.
		//
		// @since 1.69.3
		const ajaxFallback = () => {
			pmw.wpAjaxPost({
				action: "pmw_server_to_server_event",
				data  : JSON.stringify(payload),
			})
		}

		const restResponse = await pmw.tryRestOrAjax(
			{
				url : restUrl,
				init: {
					method     : "POST",
					credentials: "same-origin",
					headers    : {"Content-Type": "application/json"},
					body       : JSON.stringify(payload),
					keepalive  : true,
					redirect   : "error",
				},
			},
			ajaxFallback,
		)

		if (restResponse) {
			restResponse.json()
				.then(message => {
					message.success ? pmw.console.log(message) : pmw.console.error(message)
				})
				.catch(error => {
					pmw.console.error("Server-side send failed:", error)
				})
		}
	}

	pmw.sendEventPayloadToServerPremium = async payload => {

		const isServerToServerEnabled = () => {

			if (pmwDataLayer?.general?.server_2_server?.active) return true

			return false
		}

		const isServerToServerDisabled = () => {
			return !isServerToServerEnabled()
		}

		try {
			// If server to server is disabled, do not send payload
			if (isServerToServerDisabled()) return

			// Check if the visitor is allowed (not a bot or on IP exclusion list).
			// Resolve this synchronously whenever possible so the send below
			// (navigator.sendBeacon / keepalive fetch) is reached within the
			// page-unload window for navigation events (e.g. begin_checkout).
			// Only fall back to the async check — which may await an IP lookup —
			// when the decision isn't cached yet (typically the first event in a session).
			const visitorAllowedSync = pmw.isVisitorAllowedSync()
			if (visitorAllowedSync === false) return
			if (visitorAllowedSync === null && await pmw.isVisitorNotAllowed()) return

			pmw.console.log("Sending event to server", payload)

			// --- SSP Proxy Routing ---
			if (isSspEnabled()) {

				// Silently drop events from known proxy origins (e.g. Google Translate)
				// that the SSP would reject. No fallback — these aren't real visitors.
				if (isSspOriginBlocked()) {
					pmw.console.log(
						`SSP: blocked proxy origin — dropping event`
						+ ` (hostname: ${window.location.hostname})`
					)
					return
				}

				// Hydrate verification state from session storage on first call
				_initSspState()

				// Already verified: route directly
				if (_sspVerified === true) {
					const ok = await sendPayloadToSsp(payload)

					// If SSP returned quota exceeded, fall back for this payload
					if (!ok && _sspVerified === false && isSspFallbackEnabled()) {
						// Terminal catch: not awaited, see the standard WC routing below.
						sendPayloadViaWc(payload).catch(e => {
							pmw.console.error("Server-side send failed:", e)
						})
					}
					return
				}

				// Already failed: use WC fallback or drop
				if (_sspVerified === false) {
					if (isSspFallbackEnabled()) {
						// Terminal catch: not awaited, see the standard WC routing below.
						sendPayloadViaWc(payload).catch(e => {
							pmw.console.error("Server-side send failed:", e)
						})
					} else {
						pmw.console.log("SSP unreachable, dropping event (fallback disabled)")
					}
					return
				}

				// First event in session – verify SSP reachability
				if (!_sspVerifying) {
					_sspVerifying = true

					// Whether the SSP accepted the verification payload itself.
					// Only a payload it did not accept is re-sent on failure.
					let verificationAccepted = false

					verifySsp(payload)
						.then(success => {
							verificationAccepted = success
							_sspVerified  = success
							_sspVerifying = false

							// Persist for the rest of the browser session (survives page navigations)
							pmw.storeData("ssp_verified", success)

							if (success) {
								pmw.console.log("SSP proxy verified")
							} else {
								pmw.console.log("SSP proxy unreachable, falling back to WC")
								resendVerificationPayload(payload)
							}

							// Process any payloads queued while verifying
							drainSspQueue()
						})
						// Terminal catch: the verification chain is not awaited.
						// A chain that fails outright (e.g. the session storage
						// write throws) is treated like an unreachable SSP: the
						// state goes to false and the queued payloads drain
						// through the WC fallback, or are dropped when it is off,
						// instead of being held forever.
						.catch(e => {
							pmw.console.error("Server-side send failed (SSP verification), treating the SSP as unreachable:", e)
							failSspVerification(verificationAccepted ? null : payload)
						})
				} else {
					// Verification in progress – queue payload
					_sspPendingQueue.push(payload)
				}

				return
			}

			// --- Standard WC Routing ---
			// Terminal catch: this promise is deliberately not awaited, so the
			// enclosing try/catch cannot see a rejection from it.
			sendPayloadViaWc(payload).catch(e => {
				pmw.console.error("Server-side send failed:", e)
			})

		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * Sends transient session identifiers to the server.
	 *
	 * @param {object} payload
	 * @param {Function} [onSuccess] Called when the server confirmed the write.
	 * @return {Promise<void>}
	 *
	 * @since 1.69.3 Logs a named error on a failed request instead of the raw jqXHR object.
	 */
	pmw.sendTransientSessionIdentifiers = async (payload, onSuccess) => {

		const action = "pmw_process_transient_session_identifiers"

		try {
			pmw.wpAjaxPost({
				action : action,
				data   : payload,
				success: response => {
					pmw.console.log(response)
					if (response?.success && typeof onSuccess === "function") onSuccess()
				},
				// A WAF or security plugin rejecting the request would otherwise
				// print the whole jqXHR object, which tells nobody what failed.
				error  : (jqXHR, textStatus) => {
					pmw.console.error(
						`Sending the session identifiers failed (action: ${action}, status: ${jqXHR?.status}, statusText: ${jqXHR?.statusText || textStatus})`,
					)
				},
			})
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * Returns the ACR cookie, falling back to its pre-1.69.3 name
	 * _pmw_automatic_conversion_recovery.
	 *
	 * @returns {string} Empty when neither cookie is set
	 * @since 1.69.3
	 */
	pmw.getAcrCookie = () => pmw.getCookie("pmw_automatic_conversion_recovery") || pmw.getCookie("_pmw_automatic_conversion_recovery")

	pmw.acrRemoveCookie = () => {

		// The ACR cookie is used to verify that the purchase confirmation page has been reached

		pmw.deleteCookie("pmw_automatic_conversion_recovery")
		pmw.deleteCookie("_pmw_automatic_conversion_recovery")
	}

	pmw.getAcrOrderDetails = async () => {

		// Get the order details from the ACR cookie
		const acrOrderDetails = JSON.parse(pmw.getAcrCookie())

		const processAcrResponse = (responseJson) => {
			if (responseJson.success) {
				pmw.console.log("ACR order details retrieved successfully")
			} else {
				pmw.console.log("ACR order details could not be retrieved", responseJson)
			}

			pmwDataLayer.products = {...pmwDataLayer.products, ...responseJson.data.products}
			pmwDataLayer.order    = responseJson.data.order
		}

		const body = {
			order_id : acrOrderDetails.order_id,
			order_key: acrOrderDetails.order_key,
		}

		const restResponse = await pmw.tryRestOrAjax(
			{
				url : pmw.root + "pmw/v1/acr/order/",
				init: {
					method     : "POST",
					credentials: "same-origin",
					cache      : "no-cache",
					headers    : {"Content-Type": "application/json"},
					body       : JSON.stringify(body),
				},
			},
			() => {
				pmw.wpAjaxPost({
					action : "get_acr_order_data_ajax",
					data   : body,
					success: responseJson => processAcrResponse(responseJson),
					error  : message => pmw.console.error("ACR AJAX error", message),
				})
			},
		)

		if (restResponse) {
			const responseJson = await restResponse.json()
			processAcrResponse(responseJson)
		}
	}

}(window.pmw = window.pmw || {}))
